import React, { useState, useEffect, useRef} from 'react';
import utils from '../../utils';
import __ from '../internetLanguage';
import Pagination from './Pagination';
import WebResult from './WebResult';
// import VideoResults from './VideoResults';
import ResultsSummary from './ResultsSummary';
import NoResults from './NoResults';
import solrInstances from '../solrInstances';
import searchStates from '../../searchStates';
import spellchecker from '../services/spellchecker';
import ciosSearch from '../services/ciosSearch';
import taxSuggest from '../services/taxSuggest';
import topTerms from '../services/topTerms';
import config from '../internetConfig';
import cache from '../../services/cache';
import metrics from '../internetMetrics';

export default function WebResults({
	form,
	setFilters: setParentFilters,
	setCounts: setParentCounts,
	isActive,
	correctSpelling,
	changeTab,
	checkedFilters,
	setCheckedFilters,
	setCurrentForm,
	onTotalResultsUpdate,
}) {
	const [page, setPage] = useState(1);
	const [isNewSearch, setIsNewSearch] = useState(false);
	const [searchState, setSearchState] = useState(searchStates.READY);
	const [totalResults, setTotalResults] = useState(0);
	const [results, setResults] = useState([]);
	const [filters, setFilters] = useState({});
	const [counts, setCounts] = useState({});
	const [cioMatches, setCioMatches] = useState([]);
	const [topicMatches, setTopicMatches] = useState([]);
	const [debugData, setDebugData] = useState({});
	const data = form.data;
	const prevQueryRef = useRef('');
	const isEmpty = utils.isFormEmpty(data);
	const solr = solrInstances.en.web;
	const solrBB = solrInstances.en.webBB;
	const resultsOut = [];
	let resultIndex = 0;

	const doSearch = (pageNum) => {
		pageNum && setPage(pageNum);
		let currentPage = pageNum || page;
		let dpage = utils.toNumber(data.dpage);

		// hanlde first search when dpage is set
		if (cache.web.firstQuery) {
			if (1 < dpage) {
				currentPage = dpage;
			}
			cache.web.firstQuery = false;
			return getPreloads().then(() => {
				doSearch(currentPage);
			});
		}

		setSearchState(searchStates.SEARCHING);
		getResults(pageNum)
			.then((response) => {
				// handle spellchecker logic here
				setPage(currentPage);
				form.setDPage(currentPage);
				const useSpellchecker = config.spellchecker.enabled &&
					'true' !== data.strict &&
					1 === currentPage &&
					isNewSearch &&
					utils.formFiltersEmpty(data);
				let correction = null;
				if (useSpellchecker) {
					let spellcheckResults = spellchecker.check(data, response);
					correction = spellcheckResults.correction;
					if (spellcheckResults.suggestions || correction) {
						correctSpelling(spellcheckResults.suggestions, correction);
						if (config.debug) {
							setDebugData({
								suggestions: spellcheckResults.data.suggestions,
								collations: spellcheckResults.data.collations,
								provideCorrection: spellcheckResults.data.provideCorrection,
							});
						}
					}
					config.debug && console.info('Spellcheck info: ', spellcheckResults.data);
				}
				if (correction) {
					return getResults(pageNum, { query: correction })
						.then((correctedResponse) => {
							const {
								total: correctedTotal,
								results: correctedResults,
								filters: correctedFilters,
								counts: correctedCounts,
							} = correctedResponse;
							setResults(correctedResults);
							setTotalResults(correctedTotal);
							setFilters(correctedFilters);
							setCounts(correctedCounts);

							setParentFilters(correctedFilters);
							setParentCounts(correctedCounts);

							setSearchState(searchStates.DONE);
						})
						.catch(() => {
							setResults(response.results);
							setTotalResults(response.total);
							setSearchState(searchStates.DONE);
						});
				} else {
					setResults(response.results);
					setTotalResults(response.total);
					setFilters(response.filters);
					setCounts(response.counts);

					setParentFilters(response.filters);
					setParentCounts(response.counts);

					setSearchState(searchStates.DONE);
				}
			})
			.catch((error) => {
				config.debug && console.error('Search: Web error: ', error);
				setSearchState(searchStates.ERROR);
			});
	};

	// actions to take after search
	const postSearch = () => {
		setCioMatches(ciosSearch.getMatches());
		setTopicMatches(taxSuggest.getTerms(data.query));
		// trigger parent onTabResults for metrics
		form.onTabResults('web', page);

		// beacon for results
		if (results.length && page) {
			let resultUrls = [];
			results.forEach((result) => {
				if (result.url) {
					resultUrls.push(utils.cleanString(result.url));
				}
			});
			let metricsProps = {
				list2: resultUrls.join('|'),
				eVar63: utils.cleanTerm(data.query),
				eVar20: page,
			};
			metrics.event(null, 'cdcsitesearch-all-results', metricsProps);
			cache.init = true; // after first results page, we've initialized
		}
	};

	const getResults = (pageNum, override) => {
		return getQueryResults(pageNum, override);
	};

	// Core SOLR request, combines Best Bets with normal search
	const getQueryResults = (pageNum, override) => {
		return new Promise((resolve, reject) => {
			let currentPage = pageNum || page;
			let queries = [];
			override = Object.assign({}, { page: currentPage }, override);
			queries.push(solr.query(data, override));

			// page 1 also runs bestbets query - exlcude if using tax filters
			if (!data.checkedFilters.topic.length && !data.checkedFilters.audience.length) {
				if (1 === currentPage && config.bestBets.enabled) {
					queries.push(solrBB.query(data, override));
				}
			}

			Promise.all(queries)
				.then((responses) => {
					let response = responses.shift();
					let responseObj = Object.assign({}, response);
					if (response.error && !response.results.length) {
						return reject(response.error);
					}
					if (responses.length) {
						let bbResponse = responses.shift();
						responseObj.results = utils.mergeResults([bbResponse.results, responseObj.results]);
						responseObj.total += bbResponse.results.length;
					}
					return resolve(responseObj);
				})
				.catch((error) => {
					reject(error);
				});
		});
	};

	const clearResults = () => {
		const prevQuery = prevQueryRef.current;
		setSearchState(searchStates.READY);
		setResults([]);
		setTotalResults(0);
		setPage(1);
		setCioMatches([]);
		setTopicMatches([]);

		if (!data.query || data.query !== prevQuery) {
			prevQueryRef.current = data.query;
		}
	};

	const goToPage = (pageNum) => {
		if ('number' !== typeof pageNum || 1 > pageNum || pageNum === page) {
			return;
		}
		setResults([]);
		utils.toTop();
		doSearch(pageNum);
	};

	// Get any services that need ansynchronous requests first
	const getPreloads = () => {
		var preloads = [
			ciosSearch.load, // load CIOs
			topTerms.load, // load top terms
			// taxSuggest.load // load topic suggestions
		];
		return Promise.all(preloads);
	};

	// Initialization
	useEffect(() => {
		cache.web = {
			firstQuery: true,
		};
		if (config.ciosURL) {
			solr.addQueryFilter(ciosSearch.queryFilter);
		}
		if (config.topTerms) {
			solr.addQueryFilter(topTerms.queryFilter);
		}
	}, []);

	// On form change, update newSearch flag
	const isClearingRef = useRef(false); // Track if clearing is already in progress

	useEffect(() => {
		// Don't respond to spelling correction, they originate here
		if (form.suggestions || form.corrected) {
			return;
		}

		const prevQuery = prevQueryRef.current;

		if (!data.query && !isClearingRef.current) {
			// If query is empty and we're not already clearing, clear results and filters
			isClearingRef.current = true; // Mark clearing in progress
			clearResults();
		} else if (data.query && data.query !== prevQuery) {
			// New query detected
			clearResults(true); // Clear filters for a new query
			prevQueryRef.current = data.query; // Update the previous query
			setIsNewSearch(true); // Trigger a new search
			isClearingRef.current = false; // Reset clearing flag
		} else if (!isEmpty) {
			setIsNewSearch(true); // Trigger search for other changes
			isClearingRef.current = false; // Reset clearing flag
		} else {
			setSearchState(searchStates.READY);
			cache.init = true; // We've loaded on a blank form, so we've initialized
			isClearingRef.current = false; // Reset clearing flag
		}
	}, [data]);

	// On Active
	useEffect(() => {
		if (!isEmpty && isActive && isNewSearch) {
			doSearch();
			setIsNewSearch(false);
		}
	}, [isActive, isNewSearch]);

	// post search
	useEffect(() => {
		if (searchStates.DONE === searchState) {
			postSearch();
		}
	}, [searchState]);

	// Notify parent component of total results changes
	useEffect(() => {
		if (onTotalResultsUpdate) {
			onTotalResultsUpdate(totalResults);
		}
	}, [totalResults]); // Only depend on totalResults, not the callback function

	results.map((result) => {
		resultsOut.push(
			<WebResult key={resultIndex} resultIndex={resultIndex} result={result} page={page} query={data.query} />
		);
		resultIndex++;
	});

	return (
		<div className="results webResults" data-cdc-total={totalResults}>
			<ResultsSummary
				isEmpty={isEmpty}
				page={page}
				totalResults={totalResults}
				searchState={searchState}
				topicMatches={topicMatches}
				form={form}
				isAdvanced={true}
				addons={{ cioMatches }}
				resultType="results"
			/>
			{!results.length ? (
				<NoResults isEmpty={isEmpty} searchState={searchState} />
			) : (
				<div>
					{resultsOut}

					<Pagination page={page} totalResults={totalResults} goToPage={goToPage} anchor="#results-web" />
				</div>
			)}

			{config.debug && debugData ? (
				<div className="debug-pane">
					<dl>
						<dt>Hits</dt>
						<dd>
							<pre>{debugData.hits}</pre>
						</dd>

						<dt>
							Needed
							<br /> Correction
						</dt>
						<dd>
							<pre>{JSON.stringify(debugData.provideCorrection)}</pre>
						</dd>
						<dt>Collations</dt>
						<dd>
							<pre>{JSON.stringify(debugData.collations)}</pre>
						</dd>
						<dt>Suggestions</dt>
						<dd>
							<pre>{JSON.stringify(debugData.suggestions)}</pre>
						</dd>
					</dl>
				</div>
			) : (
				''
			)}
		</div>
	);
}
